import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Copy, FileText, MoreHorizontal, Plus, Search, Trash2 } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useFreemium } from "@/hooks/useFreemium";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FALLBACK_MINISTRY_LABEL = "General Ministry";

interface BulletinItem {
  id: string;
  label: string;
  person: string;
  time: string;
  type: "item" | "header" | "divider";
}

interface Announcement {
  id: string;
  text: string;
}

interface BulletinTemplate {
  id: string;
  church_id: string;
  event_id: string | null;
  is_template: boolean;
  name: string;
  template_name: string | null;
  service_type: string;
  service_date: string | null;
  service_time: string | null;
  offering_date: string | null;
  offering_snapshot: any | null;
  bulletin_items: BulletinItem[];
  next_week_items: BulletinItem[];
  announcements: Announcement[];
  announcements_heading?: string;
  notes: string | null;
  created_at: string;
}

export default function BulletinTemplatesPage() {
  const { profile } = useChurch();
  const { requireSubscription } = useFreemium();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const churchId = profile?.id;

  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["bulletin_templates", churchId, "templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulletin_templates")
        .select("*")
        .eq("church_id", churchId!)
        .eq("is_template", true)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((template: any) => ({
        ...template,
        bulletin_items: (Array.isArray(template.bulletin_items) ? template.bulletin_items : []) as BulletinItem[],
        next_week_items: (Array.isArray(template.next_week_items) ? template.next_week_items : []) as BulletinItem[],
        ...(() => {
          const raw = Array.isArray(template.announcements) ? template.announcements : [];
          const meta = raw.find((a: any) => a?.id === "__announcements_heading__");
          return {
            announcements: raw.filter((a: any) => a?.id !== "__announcements_heading__") as Announcement[],
            announcements_heading: typeof meta?.text === "string" ? meta.text : undefined,
          };
        })(),
      })) as BulletinTemplate[];
    },
    enabled: !!churchId,
  });

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templates;

    return templates.filter((template) => {
      const displayName = (template.template_name || template.name || "").toLowerCase();
      return (
        displayName.includes(query)
        || template.name.toLowerCase().includes(query)
        || template.service_type.toLowerCase().includes(query)
      );
    });
  }, [templates, search]);

  const openTemplateAsNewDraft = (template: BulletinTemplate) => {
    if (!requireSubscription()) return;
    navigate("/communication", {
      state: {
        prefillTemplate: template,
      },
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("bulletin_templates")
        .delete()
        .eq("id", id)
        .eq("is_template", true);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bulletin_templates"] });
      toast.success("Template deleted");
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">Templates</h2>
          <p className="text-sm text-muted-foreground">Reusable bulletin layouts you can apply to new services.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/communication")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Bulletins
          </Button>
          <Button type="button" onClick={() => navigate("/communication")}>
            <Plus className="w-4 h-4 mr-2" /> New Bulletin
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search templates..."
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="stat-card text-center py-12 text-muted-foreground">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="stat-card text-center py-12">
          <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">No templates yet. Open a bulletin and click Save as Template.</p>
          <Button type="button" variant="outline" className="mt-4" onClick={() => navigate("/communication")}>Go to Bulletins</Button>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="stat-card text-center py-12 text-muted-foreground text-sm">
          No templates match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => {
            const templateDisplayName = template.template_name || template.name;
            const itemCount = template.bulletin_items.filter((item) => item.type === "item").length;

            return (
              <Card key={template.id} className="group hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-sm font-semibold truncate">{templateDisplayName}</CardTitle>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <Badge variant="secondary" className="text-xs">{template.service_type || FALLBACK_MINISTRY_LABEL}</Badge>
                        <span className="text-xs text-muted-foreground">{format(new Date(template.created_at), "MMM d, yyyy")}</span>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-secondary transition-colors">
                          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDeleteId(template.id)} className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <p className="text-xs text-muted-foreground">{itemCount} service items · {template.announcements.length} announcements</p>
                  <Button
                    type="button"
                    size="sm"
                    className="w-full"
                    onClick={() => openTemplateAsNewDraft(template)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the template permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
